import React, { useEffect, useState } from 'react';
import { useParams, useLocation, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { dbSelect, dbInsert } from '../../lib/supabase';
import { blogSeedData } from '../../data/blogSeedData';

const BlogListPage = ({ filterType = null }) => {
  const { category, tag } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  // Parse URL search parameters (e.g. ?q=react)
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const q = searchParams.get('q');
    if (q) {
      setSearchQuery(q);
    } else if (filterType !== 'search') {
      setSearchQuery('');
    }
  }, [location.search, filterType]);

  useEffect(() => {
    const loadBlogs = async () => {
      setLoading(true);
      try {
        let { data, error } = await dbSelect('blogs', 'publishedDate', false);
        // If empty, auto-seed the database
        if (!data || data.length === 0) {
          console.log("Seeding blog database with initial data...");
          const seeded = [];
          for (const item of blogSeedData) {
            const { data: res } = await dbInsert('blogs', item);
            if (res && res[0]) {
              seeded.push(res[0]);
            }
          }
          setBlogs(seeded.length > 0 ? seeded : blogSeedData);
        } else {
          // Filter only published blogs for the public list
          setBlogs(data.filter(b => b.status === 'published' || !b.status));
        }
      } catch (err) {
        console.error("Error fetching blogs:", err);
        setBlogs(blogSeedData);
      } finally {
        setLoading(false);
      }
    };
    loadBlogs();
  }, []);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/blog/search?q=${encodeURIComponent(searchQuery.trim())}`);
    } else {
      navigate('/blog');
    }
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await dbInsert('newsletter_subscriptions', { email });
      setSubscribed(true);
      setEmail('');
      setTimeout(() => setSubscribed(false), 5000);
    } catch (err) {
      console.error("Error subscribing:", err);
      setSubscribed(true); // Treat as pass for UI fallback
    }
  };

  // Filter Blogs based on route parameters
  const getFilteredBlogs = () => {
    let list = [...blogs];

    if (filterType === 'category' && category) {
      list = list.filter(b => b.category?.toLowerCase() === category.toLowerCase());
    } else if (filterType === 'tag' && tag) {
      list = list.filter(b => (b.tags || []).some(t => t.toLowerCase() === tag.toLowerCase()));
    } else if (filterType === 'search') {
      const searchParams = new URLSearchParams(location.search);
      const query = (searchParams.get('q') || searchQuery || '').toLowerCase().trim();
      if (query) {
        list = list.filter(b =>
          b.title?.toLowerCase().includes(query) ||
          b.content?.toLowerCase().includes(query) ||
          b.category?.toLowerCase().includes(query) ||
          (b.tags || []).some(t => t.toLowerCase().includes(query)) ||
          b.author?.toLowerCase().includes(query)
        );
      }
    }
    return list;
  };

  const filteredBlogs = getFilteredBlogs();

  // Extract featured & popular posts
  const featuredPost = filteredBlogs.find(b => b.isFeatured) || filteredBlogs[0];
  const remainingPosts = filteredBlogs.filter(b => b.id !== featuredPost?.id);
  const popularPosts = [...blogs].sort((a, b) => (b.views || 0) - (a.views || 0)).slice(0, 4);

  // Extract unique categories & tags for sidebar widgets
  const categoriesList = Array.from(new Set(blogs.map(b => b.category).filter(Boolean)));
  const allTags = Array.from(new Set(blogs.flatMap(b => b.tags || []).filter(Boolean)));

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Setup dynamic Helmet Header
  let seoTitle = 'Byte Pairs Blog | Software, AI & Automation Insights';
  let seoDesc = 'Read engineering resources, GEO/AEO blueprints, SaaS valuations, and automated workflow guides from the developer team at Byte Pairs.';

  if (filterType === 'category' && category) {
    seoTitle = `Articles on ${category} | Byte Pairs Blog`;
    seoDesc = `Explore custom development insights, automations, and technology strategies regarding ${category}.`;
  } else if (filterType === 'tag' && tag) {
    seoTitle = `Tagged with #${tag} | Byte Pairs Blog`;
    seoDesc = `Technical tutorials and software design insights related to ${tag}.`;
  } else if (filterType === 'search') {
    seoTitle = 'Search Results | Byte Pairs Blog';
  }

  const pageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": seoTitle,
    "description": seoDesc,
    "url": "https://bytepairs.com/blog"
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://bytepairs.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Blog",
        "item": "https://bytepairs.com/blog"
      }
    ]
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Byte Pairs",
    "url": "https://bytepairs.com/",
    "logo": "https://bytepairs.com/android-chrome-512x512.png"
  };

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDesc} />
        <link rel="canonical" href="https://bytepairs.com/blog" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDesc} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://bytepairs.com/blog" />
        <meta property="og:image" content="https://bytepairs.com/android-chrome-512x512.png" />
        <script type="application/ld+json">
          {JSON.stringify(pageSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-background text-foreground">
        <Header />

        <main className="pt-24 pb-20">
          {/* Blog Header & Search Hero */}
          <section className="py-12 bg-gradient-to-br from-primary/5 via-muted/20 to-accent/5 border-b border-border">
            <div className="max-w-6xl mx-auto px-6 text-center">
              <span className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-3 py-1.5 rounded-full text-xs font-semibold mb-4">
                <Icon name="BookOpen" size={14} />
                <span>Byte Pairs Journal</span>
              </span>
              <h1 className="text-3xl md:text-5xl font-black tracking-tight mb-4 leading-tight">
                Software & AI <span className="text-gradient">Engineering Log</span>
              </h1>
              <p className="text-text-secondary text-sm md:text-base max-w-xl mx-auto mb-8">
                Factual templates, regional blueprints, and performance case studies curated directly by our developer team.
              </p>

              {/* Search Bar */}
              <form onSubmit={handleSearchSubmit} className="max-w-md mx-auto relative">
                <input
                  type="text"
                  placeholder="Search articles by title, tag, or technology..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-5 py-3.5 bg-card border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary pr-12 shadow-sm"
                />
                <button type="submit" className="absolute right-4 top-1/2 -translate-y-1/2 text-text-secondary hover:text-primary transition-colors">
                  <Icon name="Search" size={18} />
                </button>
              </form>
            </div>
          </section>

          {/* Active Filter Indicators */}
          {(category || tag || filterType === 'search') && (
            <div className="max-w-6xl mx-auto px-6 pt-8 flex items-center justify-between text-sm">
              <div className="flex items-center space-x-2 text-text-secondary">
                <Icon name="Sliders" size={16} />
                <span>Showing articles for:</span>
                <span className="bg-primary/10 text-primary px-3 py-1 rounded-full font-semibold text-xs uppercase">
                  {category ? `Category: ${category}` : tag ? `Tag: #${tag}` : `Search Query: "${new URLSearchParams(location.search).get('q')}"`}
                </span>
              </div>
              <Link to="/blog" className="text-xs text-primary hover:text-accent font-semibold transition-colors flex items-center space-x-1">
                <span>Clear Filters</span>
                <Icon name="X" size={12} />
              </Link>
            </div>
          )}

          {/* Main Layout Grid */}
          <div className="max-w-6xl mx-auto px-6 pt-10">
            <h2 className="sr-only">
              {category ? `Articles in ${category}` : tag ? `Articles tagged #${tag}` : 'Latest Articles'}
            </h2>
            {loading ? (
              <div className="flex flex-col justify-center items-center py-24 space-y-4">
                <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                <p className="text-text-secondary text-sm">Loading articles database...</p>
              </div>
            ) : filteredBlogs.length === 0 ? (
              <div className="text-center py-24 border border-dashed border-border rounded-3xl bg-card/50">
                <Icon name="BookOpen" size={48} className="mx-auto text-slate-500 mb-4" />
                <h3 className="text-lg font-bold text-foreground mb-2">No Articles Found</h3>
                <p className="text-text-secondary text-sm max-w-sm mx-auto mb-6">
                  No articles matched your active filters or search terms. Try modifying your query.
                </p>
                <Link to="/blog" className="inline-flex items-center space-x-2 bg-primary text-white px-5 py-2.5 rounded-xl text-sm font-semibold hover:shadow-brand-md transition-all">
                  <span>View All Posts</span>
                </Link>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">

                {/* Left Side: Articles Content */}
                <div className="lg:col-span-8 space-y-12">

                  {/* Featured Post (Only displayed if listing general pages without query) */}
                  {!category && !tag && filterType !== 'search' && featuredPost && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="group border border-border bg-card overflow-hidden rounded-3xl hover:border-primary/40 hover:shadow-brand-md transition-all duration-300 grid grid-cols-1 md:grid-cols-12"
                    >
                      <div className="md:col-span-6 bg-slate-950 flex items-center justify-center border-b md:border-b-0 md:border-r border-border min-h-[250px] relative overflow-hidden">
                        {featuredPost.ogImage ? (
                          <img
                            src={featuredPost.ogImage}
                            alt={featuredPost.title}
                            className="absolute inset-0 w-full h-full object-contain group-hover:scale-102 transition-transform duration-500 p-2"
                          />
                        ) : (
                          <Icon name="Cpu" size={64} className="text-primary group-hover:scale-110 transition-transform duration-500" />
                        )}
                      </div>
                      <div className="md:col-span-6 p-8 flex flex-col justify-between">
                        <div>
                          <div className="flex items-center space-x-3 text-xs mb-4">
                            <Link to={`/blog/category/${featuredPost.category?.toLowerCase()}`} className="text-primary font-bold uppercase hover:underline">
                              {featuredPost.category}
                            </Link>
                            <span className="text-slate-500">•</span>
                            <span className="text-text-secondary">{featuredPost.readingTime} min read</span>
                          </div>
                          <h2 className="text-2xl font-bold mb-3 group-hover:text-primary transition-colors leading-tight">
                            <Link to={`/blog/${featuredPost.slug}`}>
                              {featuredPost.title}
                            </Link>
                          </h2>
                          <p className="text-text-secondary text-sm line-clamp-3 leading-relaxed mb-6">
                            {featuredPost.metaDescription}
                          </p>
                        </div>
                        <div className="flex items-center justify-between text-xs pt-4 border-t border-border/60">
                          <span className="font-semibold text-foreground">{featuredPost.author}</span>
                          <span className="text-text-secondary">{formatDate(featuredPost.publishedDate)}</span>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {/* List of remaining posts */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {(category || tag || filterType === 'search' ? filteredBlogs : remainingPosts).map((post) => (
                      <article
                        key={post.id}
                        className="group border border-border bg-card rounded-2xl p-6 hover:border-primary/40 hover:shadow-brand-md transition-all duration-300 flex flex-col justify-between h-[340px]"
                      >
                        <div>
                          <div className="flex items-center space-x-3 text-xs mb-3">
                            <Link to={`/blog/category/${post.category?.toLowerCase()}`} className="text-primary font-bold uppercase hover:underline">
                              {post.category}
                            </Link>
                            <span className="text-slate-500">•</span>
                            <span className="text-text-secondary">{post.readingTime} min read</span>
                          </div>
                          <h3 className="font-bold text-lg mb-2 group-hover:text-primary transition-colors line-clamp-2 leading-tight">
                            <Link to={`/blog/${post.slug}`}>
                              {post.title}
                            </Link>
                          </h3>
                          <p className="text-text-secondary text-xs md:text-sm line-clamp-3 leading-relaxed">
                            {post.metaDescription}
                          </p>
                        </div>
                        <div className="pt-4 border-t border-border/60 flex items-center justify-between text-xs">
                          <span className="font-semibold text-foreground">{post.author}</span>
                          <span className="text-text-secondary">{formatDate(post.publishedDate)}</span>
                        </div>
                      </article>
                    ))}
                  </div>

                </div>

                {/* Right Side: Sidebar Widgets */}
                <aside className="lg:col-span-4 space-y-8">

                  {/* Popular Posts Widget */}
                  <div className="p-6 bg-card border border-border rounded-2xl">
                    <h4 className="font-bold text-lg mb-4 text-foreground flex items-center space-x-2 border-b border-border pb-3">
                      <Icon name="TrendingUp" size={18} className="text-primary" />
                      <span>Popular Insights</span>
                    </h4>
                    <div className="space-y-4">
                      {popularPosts.map((post) => (
                        <div key={post.id} className="group flex items-start space-x-3 text-sm">
                          <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:bg-primary/10 transition-colors">
                            <Icon name="FileText" size={14} className="text-text-secondary group-hover:text-primary" />
                          </div>
                          <div>
                            <h5 className="font-semibold text-foreground group-hover:text-primary line-clamp-2 leading-snug transition-colors">
                              <Link to={`/blog/${post.slug}`}>{post.title}</Link>
                            </h5>
                            <span className="text-[11px] text-text-secondary">{post.readingTime} min read</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Categories Widget */}
                  <div className="p-6 bg-card border border-border rounded-2xl">
                    <h4 className="font-bold text-lg mb-4 text-foreground flex items-center space-x-2 border-b border-border pb-3">
                      <Icon name="Folder" size={18} className="text-primary" />
                      <span>Categories</span>
                    </h4>
                    <div className="flex flex-col space-y-2">
                      {categoriesList.map((cat, idx) => (
                        <Link
                          key={idx}
                          to={`/blog/category/${cat.toLowerCase()}`}
                          className="flex items-center justify-between text-sm text-text-secondary hover:text-primary transition-colors py-1 border-b border-border/30 last:border-b-0"
                        >
                          <span>{cat}</span>
                          <span className="text-[10px] bg-muted px-2 py-0.5 rounded-full text-slate-500 font-semibold">
                            {blogs.filter(b => b.category === cat).length}
                          </span>
                        </Link>
                      ))}
                    </div>
                  </div>

                  {/* Tags Widget */}
                  <div className="p-6 bg-card border border-border rounded-2xl">
                    <h4 className="font-bold text-lg mb-4 text-foreground flex items-center space-x-2 border-b border-border pb-3">
                      <Icon name="Tag" size={18} className="text-primary" />
                      <span>Tag Cloud</span>
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {allTags.map((t, idx) => (
                        <Link
                          key={idx}
                          to={`/blog/tag/${t.toLowerCase()}`}
                          className="text-xs bg-muted hover:bg-primary/10 text-text-secondary hover:text-primary border border-border/80 px-2.5 py-1 rounded-lg transition-colors font-medium"
                        >
                          #{t}
                        </Link>
                      ))}
                    </div>
                  </div>

                  {/* Newsletter Signup Widget */}
                  <div className="p-6 bg-gradient-to-br from-primary/10 to-accent/5 border border-primary/20 rounded-2xl text-center">
                    <div className="w-12 h-12 bg-primary/20 text-primary rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <Icon name="MailOpen" size={22} />
                    </div>
                    <h4 className="font-bold text-lg text-foreground mb-2">Subscribe to Log</h4>
                    <p className="text-text-secondary text-xs leading-relaxed mb-4 max-w-xs mx-auto">
                      Receive weekly insights on software architecture, AEO blueprints, and pricing strategies directly in your inbox.
                    </p>
                    <form onSubmit={handleSubscribe} className="space-y-2">
                      <input
                        type="email"
                        required
                        placeholder="yourname@domain.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full px-3 py-2 bg-card border border-border rounded-xl text-xs focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary text-center"
                      />
                      <button
                        type="submit"
                        className="w-full bg-primary hover:bg-accent text-white py-2 rounded-xl text-xs font-semibold hover:shadow-brand-sm transition-all duration-300"
                      >
                        {subscribed ? 'Subscribed! ✅' : 'Join Engineering Log'}
                      </button>
                    </form>
                  </div>

                </aside>

              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default BlogListPage;
